import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'react-hot-toast';
import { Code2, Plus, Pencil, Trash2, RefreshCw, Power } from 'lucide-react';
import { clientScriptsApi } from '../services/clientScriptsApi';

const EVENTS = [
  { value: 'onload', label: 'On Load (form opens)' },
  { value: 'validate', label: 'Validate (block save)' },
  { value: 'before_save', label: 'Before Save (mutate doc)' },
];

const EVENT_BADGE = {
  onload: 'bg-orange-100 text-orange-700',
  validate: 'bg-amber-100 text-amber-700',
  before_save: 'bg-green-100 text-green-700',
};

const SAMPLE = `// Available API: frm.doc, frm.get_value(field),
// frm.set_value(field, value), frm.msgprint(text), frm.add_error(text)
// Example (validate): block save when amount is missing
if (!frm.get_value('amount')) {
  frm.add_error('Amount is required');
}`;

const emptyForm = () => ({
  doctype: '', name: '', event: 'onload', code: SAMPLE, active: true, description: '',
});

const ClientScriptManager = () => {
  const [doctypes, setDoctypes] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const loadAll = async () => {
    setLoading(true);
    try {
      const [dt, sc] = await Promise.all([
        clientScriptsApi.doctypes(),
        clientScriptsApi.list(),
      ]);
      setDoctypes(dt.data || []);
      setScripts(sc.data || []);
    } catch {
      toast.error('Failed to load client scripts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditingId(s.script_id);
    setForm({ doctype: s.doctype, name: s.name, event: s.event, code: s.code, active: s.active, description: s.description || '' });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.doctype) { toast.error('Select a DocType'); return; }
    if (!form.name.trim()) { toast.error('Enter a script name'); return; }
    if (!form.code.trim()) { toast.error('Script code cannot be empty'); return; }
    try {
      if (editingId) {
        await clientScriptsApi.update(editingId, form);
        toast.success('Script updated');
      } else {
        await clientScriptsApi.create(form);
        toast.success('Script created');
      }
      setDialogOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save script');
    }
  };

  const toggleActive = async (s) => {
    try {
      await clientScriptsApi.update(s.script_id, { active: !s.active });
      loadAll();
    } catch { toast.error('Failed to toggle'); }
  };

  const remove = async (s) => {
    try { await clientScriptsApi.remove(s.script_id); toast.success('Deleted'); loadAll(); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Code2 className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Client Scripts</h1>
              <p className="text-sm text-gray-500">Browser-side form automations (run on user-defined DocType records)</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadAll}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={openCreate} data-testid="new-script-btn">
              <Plus className="w-4 h-4 mr-2" />New Script
            </Button>
          </div>
        </div>

        <Card className="p-5">
          {loading ? (
            <div className="h-40 flex items-center justify-center text-gray-400">Loading…</div>
          ) : scripts.length === 0 ? (
            <div className="h-40 flex flex-col items-center justify-center text-gray-400">
              <Code2 className="w-10 h-10 mb-2" />
              <p className="text-sm">No client scripts yet. Create one to automate form behavior.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {scripts.map((s) => (
                <div key={s.script_id} className="flex items-center gap-3 py-3" data-testid={`script-row-${s.script_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{s.name}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${EVENT_BADGE[s.event]}`}>{s.event}</span>
                      <span className="text-xs text-gray-400">{s.doctype}</span>
                      {!s.active && <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">inactive</span>}
                    </div>
                    {s.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{s.description}</p>}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => toggleActive(s)} title={s.active ? 'Disable' : 'Enable'}>
                    <Power className={`w-4 h-4 ${s.active ? 'text-green-600' : 'text-gray-300'}`} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-script-${s.script_id}`}>
                    <Pencil className="w-4 h-4 text-orange-600" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(s)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Client Script' : 'New Client Script'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">DocType *</Label>
                  <Select value={form.doctype} onValueChange={(v) => setForm({ ...form, doctype: v })}>
                    <SelectTrigger data-testid="script-doctype-select"><SelectValue placeholder="Select DocType" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {doctypes.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Event *</Label>
                  <Select value={form.event} onValueChange={(v) => setForm({ ...form, event: v })}>
                    <SelectTrigger data-testid="script-event-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EVENTS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Require amount" data-testid="script-name-input" />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional" />
              </div>
              <div>
                <Label className="text-xs">JavaScript</Label>
                <textarea
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  data-testid="script-code-input"
                  spellCheck={false}
                  rows={10}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md font-mono text-xs bg-slate-900 text-slate-100 focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  API: <code>frm.doc</code>, <code>frm.get_value(f)</code>, <code>frm.set_value(f,v)</code>, <code>frm.msgprint(t)</code>, <code>frm.add_error(t)</code>. Return <code>false</code> in validate to block save.
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} data-testid="script-active-checkbox" />
                <span className="text-sm">Active</span>
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button className="bg-orange-600 hover:bg-orange-700" onClick={save} data-testid="save-script-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ClientScriptManager;
